import InfoIcon from './InfoIcon.jsx';
import Pill from './Pill.jsx';
import './SportCard.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function genderLabel(g) {
  if (g === 'coed')          return 'Coed';
  if (g === 'male')          return 'Boys';
  if (g === 'female')        return 'Girls';
  if (g === 'coed-separate') return 'Boys & Girls (separate teams)';
  return g;
}

// ── Variant: 6th-grade PE option ──────────────────────────────────────────

function SixthGradeCard({ sport }) {
  const isRunningClub = sport.id === 'running-club-6';
  return (
    <article className="sport-card sport-card--6th">
      <div className="sport-card-header">
        <h3 className="sport-card-name">{sport.name}</h3>
        <InfoIcon source={sport.source} />
      </div>

      <div className="sport-card-pills">
        <Pill variant={sport.replacesPE ? 'primary' : 'default'} size="sm">
          {sport.replacesPE ? 'Replaces PE' : 'After-school activity'}
        </Pill>
        {sport.gender === 'separate' && (
          <Pill variant="default" size="sm">Boys & Girls (separate)</Pill>
        )}
        {sport.gender === 'coed' && (
          <Pill variant="default" size="sm">Coed</Pill>
        )}
      </div>

      <p className="sport-card-description">{sport.description}</p>

      {isRunningClub && sport.meetingTime && (
        <p className="sport-card-early-time">
          <span className="sport-card-early-label">Early start:</span>{' '}
          {sport.meetingTime} — before first class
        </p>
      )}

      {sport.enrollment && (
        <p className="sport-card-enrollment">{sport.enrollment}</p>
      )}

      {sport.notes && !isRunningClub && (
        <p className="sport-card-notes">{sport.notes}</p>
      )}
    </article>
  );
}

// ── Variant: UIL sport ────────────────────────────────────────────────────

function UilCard({ sport }) {
  return (
    <article className="sport-card sport-card--uil">
      <div className="sport-card-header">
        <h3 className="sport-card-name">{sport.name}</h3>
        <InfoIcon source={sport.source} />
      </div>

      <div className="sport-card-pills">
        <Pill variant="accent" size="sm">{sport.season}</Pill>
        {sport.noCut && (
          <Pill variant="success" size="sm">No tryouts</Pill>
        )}
        <Pill variant="default" size="sm">{genderLabel(sport.gender)}</Pill>
      </div>

      <div className="sport-card-schedule">
        {sport.practiceSchedule && (
          <p className="sport-card-schedule-row">
            <span className="sport-card-schedule-label">Practice:</span>{' '}
            {sport.practiceSchedule}
          </p>
        )}
        {sport.gameSchedule && (
          <p className="sport-card-schedule-row">
            <span className="sport-card-schedule-label">Games:</span>{' '}
            {sport.gameSchedule}
          </p>
        )}
      </div>

      {!sport.noCut && sport.tryouts && (
        <p className="sport-card-tryouts">{sport.tryouts}</p>
      )}

      {sport.enrollment && (
        <details className="sport-card-details">
          <summary>Enrollment & requirements</summary>
          <p className="sport-card-details-body">{sport.enrollment}</p>
          {sport.requirements && sport.requirements.length > 0 && (
            <ul className="sport-card-req-list">
              {sport.requirements.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </details>
      )}

      {sport.notes && (
        <p className="sport-card-notes">{sport.notes}</p>
      )}
    </article>
  );
}

// ── Variant: Performance group ────────────────────────────────────────────

function PerformanceCard({ sport }) {
  return (
    <article className="sport-card sport-card--performance">
      <div className="sport-card-header">
        <h3 className="sport-card-name">{sport.name}</h3>
        <InfoIcon source={sport.source} />
      </div>

      <div className="sport-card-pills">
        <Pill variant="default" size="sm">Year-long</Pill>
        {sport.tryoutsRequired ? (
          <Pill variant="accent" size="sm">Tryouts required</Pill>
        ) : (
          <Pill variant="success" size="sm">Open enrollment</Pill>
        )}
      </div>

      {sport.tryoutsTiming && (
        <p className="sport-card-tryout-timing">
          <span className="sport-card-schedule-label">Tryout timing:</span>{' '}
          {sport.tryoutsTiming}
        </p>
      )}

      {sport.tryoutSteps && (
        <p className="sport-card-tryout-steps">{sport.tryoutSteps}</p>
      )}

      {sport.coaches && sport.coaches !== 'TBA' && (
        <p className="sport-card-coach">
          <span className="sport-card-schedule-label">Coach:</span>{' '}
          {sport.coachEmail ? (
            <a href={`mailto:${sport.coachEmail}`} className="sport-card-coach-link">
              {sport.coaches}
            </a>
          ) : (
            sport.coaches
          )}
        </p>
      )}

      {sport.performsAt && sport.performsAt.length > 0 && (
        <p className="sport-card-performs">
          <span className="sport-card-schedule-label">Performs at:</span>{' '}
          {sport.performsAt.join(', ')}
        </p>
      )}

      {sport.practices && (
        <p className="sport-card-schedule-row">
          <span className="sport-card-schedule-label">Practice:</span>{' '}
          {sport.practices}
        </p>
      )}

      {sport.enrollment && (
        <p className="sport-card-enrollment">{sport.enrollment}</p>
      )}
    </article>
  );
}

// ── Public component ──────────────────────────────────────────────────────

export default function SportCard({ sport, variant }) {
  if (variant === '6th')         return <SixthGradeCard sport={sport} />;
  if (variant === 'uil')         return <UilCard sport={sport} />;
  if (variant === 'performance') return <PerformanceCard sport={sport} />;
  return null;
}
